import { useMemo, useCallback, } from 'react';
import { omitBy, isUndefined, pick, } from 'lodash';

import firebase from '../../firebase';
import { applyChangeToList, removeChangeFromList, applyChanges, } from '../../shared/models/simulation';
import useQueryParams from './useQueryParams';
import useDocumentSubscription from './useDocumentSubscription';
import useCollectionSubscriptionInTenant from './useCollectionSubscriptionInTenant';
import useFirebaseUser from './useFirebaseUser';

const db = firebase.firestore();
const simulationsRef = db.collection('simulations');

// 在庫シミュレーション。
// マスタのデータには一切触らず、変更内容を simulation.changes に貯める。
// 各画面はマスタを読んだうえで applyChanges を通して表示する。
export default function useSimulation() {
  const { simulationId } = useQueryParams();
  const { firebaseUser } = useFirebaseUser();
  const simulationRef = useMemo(
    _ => simulationId ? simulationsRef.doc(simulationId) : null,
    [simulationId]
  );
  const simulation = useDocumentSubscription(simulationRef, [simulationId]);
  const isSimulating = simulationRef != null;
  // 読込中は undefined、削除済みなら null
  const isSimulationMissing = isSimulating && simulation === null;
  // 適用済み・失効済みは閲覧のみ
  const isStale = simulation?.appliedAt != null || simulation?.staleAt != null;
  const isEditable = isSimulating && simulation != null && !isStale;

  const changes = useMemo(_ => simulation?.changes ?? [], [simulation?.changes]);

  // マスタをロックしている有効なシミュレーション。
  // 1つでもあればマスタ側の編集を止める
  const activeSimulations = useCollectionSubscriptionInTenant(
    simulationsRef.where('appliedAt', '==', null).where('staleAt', '==', null)
  );
  // マスタを見ているときに有効なシミュレーションが1件でもあればロック中。
  // 複数あればその全てを適用/破棄しないと解除されない
  const isMasterLocked = !isSimulating && activeSimulations.length > 0;

  const operatedBy = useCallback(
    _ => omitBy(pick(firebaseUser, ['uid', 'email', 'displayName']), isUndefined),
    [firebaseUser]
  );

  // 変更を記録する。マスタには書き込まない
  const recordChange = useCallback(async (collectionName, type, id, values = null) => {
    if (!simulationRef) throw new Error('シミュレーション中ではありません');
    const next = applyChangeToList(changes, {
      collectionName,
      type,
      id,
      values,
      changedAt: new Date(),
      changedBy: operatedBy(),
    });
    await simulationRef.update({ changes: next, updatedAt: new Date(), });
  }, [simulationRef, changes, operatedBy]);

  const removeChange = useCallback(async (collectionName, id) => {
    if (!simulationRef) return;
    await simulationRef.update({
      changes: removeChangeFromList(changes, collectionName, id),
      updatedAt: new Date(),
    });
  }, [simulationRef, changes]);

  // マスタの一覧に変更を重ねる。各画面はこれを通してから表示・計算する
  const withChanges = useCallback((masterItems, collectionName) => {
    return isSimulating ? applyChanges(masterItems, changes, collectionName) : masterItems;
  }, [isSimulating, changes]);

  const changeOf = useCallback((collectionName, id) => {
    return changes.find(_ => _.collectionName === collectionName && _.id === id);
  }, [changes]);

  // タブ間の行き来でシミュレーションを引き継ぐ
  const withSimulationId = useCallback((path) => {
    if (!simulationId) return path;
    return `${path}${path.includes('?') ? '&' : '?'}simulationId=${simulationId}`;
  }, [simulationId]);

  return {
    simulationId,
    simulation,
    simulationRef,
    isSimulating,
    isSimulationMissing,
    isStale,
    isEditable,
    isMasterLocked,
    changes,
    recordChange,
    removeChange,
    withChanges,
    changeOf,
    withSimulationId,
    operatedBy,
  };
};
